import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabase';

// Simple business shape for the scroll strip.
// This mirrors the key data that the WP Traders_Scroll_Widget uses:
// title (store_name), thumbnail (avatar_url), and permalink (slug-based route),
// but extended to support traders, crafties, and hospitality businesses.
interface SliderTrader {
  id: number;
  store_name: string;
  slug?: string | null;
  avatar_url?: string | null;
  location?: string | null;
  verified?: boolean | null;
  created_at?: string | null;
  businessType: 'trader' | 'craftsman' | 'hospitality';
}

// Props allow us to reuse this strip in different contexts while
// keeping defaults aligned with the WP widget behavior.
interface TraderScrollStripProps {
  // Maximum number of unique traders to display before duplicating for the loop
  limit?: number;
  // Display mode equivalent to the Elementor control: latest vs random
  mode?: 'latest' | 'random';
  // Text for the CTA button on each card
  buttonText?: string;
  // Visual color scheme similar to the widget (light / dark card backgrounds)
  colorScheme?: 'light' | 'dark';
}

const TraderScrollStrip: React.FC<TraderScrollStripProps> = ({
  limit = 6,
  mode = 'latest',
  buttonText = 'Read More',
  colorScheme = 'light'
}) => {
  const [traders, setTraders] = useState<SliderTrader[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Refs used for the infinite horizontal scroll animation
  const containerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Used to pause the scroll when the user hovers the strip
  const isHoveredRef = useRef<boolean>(false);

  // Utility: shallow shuffle to approximate the Elementor "random" mode
  const shuffleTraders = (items: SliderTrader[]): SliderTrader[] => {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  // Fetch a small set of verified businesses from Supabase across
  // all three tables (traders, crafties, hospitality).
  // This mirrors the widget idea of showing latest or random entries,
  // but keeps the query simple and efficient for the SPA.
  useEffect(() => {
    let isMounted = true;

    const fetchTraders = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch up to 20 latest verified OR mockup entries from each table.
        // We then merge and sort them so the strip can show a
        // combined view of the most recent businesses.
        const [tradersResult, craftiesResult, hospitalityResult] = await Promise.all([
          supabase
            .from('traders')
            .select('id, store_name, slug, avatar_url, location, verified, created_at')
            .or('verified.eq.true,mockup.eq.true')
            .order('created_at', { ascending: false })
            .limit(20),
          supabase
            .from('crafties')
            .select('id, store_name, slug, avatar_url, location, verified, created_at')
            .or('verified.eq.true,mockup.eq.true')
            .order('created_at', { ascending: false })
            .limit(20),
          supabase
            .from('hospitality')
            .select('id, store_name, slug, avatar_url, location, verified, created_at')
            .or('verified.eq.true,mockup.eq.true')
            .order('created_at', { ascending: false })
            .limit(20)
        ]);

        if (tradersResult.error) throw tradersResult.error;
        if (craftiesResult.error) throw craftiesResult.error;
        if (hospitalityResult.error) throw hospitalityResult.error;

        if (!isMounted) {
          return;
        }

        const combined: SliderTrader[] = [];

        if (tradersResult.data) {
          for (const row of tradersResult.data as any[]) {
            combined.push({
              id: row.id,
              store_name: row.store_name,
              slug: row.slug,
              avatar_url: row.avatar_url,
              location: row.location,
              verified: row.verified,
              created_at: row.created_at,
              businessType: 'trader'
            });
          }
        }

        if (craftiesResult.data) {
          for (const row of craftiesResult.data as any[]) {
            combined.push({
              id: row.id,
              store_name: row.store_name,
              slug: row.slug,
              avatar_url: row.avatar_url,
              location: row.location,
              verified: row.verified,
              created_at: row.created_at,
              businessType: 'craftsman'
            });
          }
        }

        if (hospitalityResult.data) {
          for (const row of hospitalityResult.data as any[]) {
            combined.push({
              id: row.id,
              store_name: row.store_name,
              slug: row.slug,
              avatar_url: row.avatar_url,
              location: row.location,
              verified: row.verified,
              created_at: row.created_at,
              businessType: 'hospitality'
            });
          }
        }

        // Sort by created_at descending so the newest are first.
        combined.sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bTime - aTime;
        });

        let processed: SliderTrader[] = combined;

        if (mode === 'random') {
          processed = shuffleTraders(processed).slice(0, limit);
        } else {
          processed = processed.slice(0, limit);
        }

        setTraders(processed);
      } catch (err: any) {
        console.error('Error loading traders for scroll strip:', err);
        if (isMounted) {
          setError('Failed to load traders');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTraders();

    return () => {
      isMounted = false;
    };
  }, [limit, mode]);

  // Set up the infinite horizontal scroll animation.
  // We duplicate the trader cards in the DOM and translate the track
  // leftwards; once we have scrolled past the first set, we reset to 0.
  useEffect(() => {
    const track = trackRef.current;
    const container = containerRef.current;

    if (!track || !container || traders.length === 0) {
      return;
    }

    let animationFrame: number | null = null;
    let lastTimestamp: number | null = null;
    let position = 0;

    // Measure the width of the first set of cards so we know when
    // to reset the translateX back to 0 for a seamless loop.
    const singleSetWidth = track.scrollWidth / 2;

    const speedPxPerSecond = 40; // Horizontal speed of the strip

    const step = (timestamp: number) => {
      if (isHoveredRef.current) {
        // If the user is hovering the strip, pause animation but
        // keep requesting frames so that we resume smoothly.
        animationFrame = requestAnimationFrame(step);
        return;
      }

      if (lastTimestamp == null) {
        lastTimestamp = timestamp;
      }

      const deltaSeconds = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      // Move the track left by updating translateX
      position -= speedPxPerSecond * deltaSeconds;

      // Once we have scrolled past one full set of cards, reset.
      if (Math.abs(position) >= singleSetWidth) {
        position = 0;
      }

      track.style.transform = `translateX(${position}px)`;

      animationFrame = requestAnimationFrame(step);
    };

    animationFrame = requestAnimationFrame(step);

    return () => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [traders]);

  // Helper for avatar: fall back to initials-based placeholder if no image
  const getAvatar = (trader: SliderTrader) => {
    if (trader.avatar_url) {
      return trader.avatar_url;
    }
    const seed = encodeURIComponent(trader.store_name || 'Business');
    return `https://api.dicebear.com/7.x/initials/svg?seed=${seed}&backgroundColor=f3f4f6&fontSize=40`;
  };

  // Helper to build the correct detail URL per business type.
  const getDetailLink = (trader: SliderTrader) => {
    const slugOrId = trader.slug ?? trader.id;

    switch (trader.businessType) {
      case 'trader':
        return `/trader/${slugOrId}`;
      case 'craftsman':
        return `/craftsman/${slugOrId}`;
      case 'hospitality':
        return `/hospitality/${slugOrId}`;
      default:
        return `/trader/${slugOrId}`;
    }
  };

  // Helper to get a human-readable label for the business type badge.
  const getBusinessTypeLabel = (type: SliderTrader['businessType']) => {
    switch (type) {
      case 'trader':
        return 'Trader';
      case 'craftsman':
        return 'Craftie';
      case 'hospitality':
        return 'Hospitality';
      default:
        return 'Trader';
    }
  };

  // Helper to get Tailwind classes for the business type badge.
  const getBusinessTypeBadgeClass = (type: SliderTrader['businessType']) => {
    switch (type) {
      case 'trader':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'craftsman':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'hospitality':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  const cardBackgroundClass =
    colorScheme === 'dark'
      ? 'bg-[#121212] border-[#333333]'
      : 'bg-white border-gray-200';

  const nameOverlayClass =
    colorScheme === 'dark'
      ? 'bg-black/50 text-white'
      : 'bg-black/40 text-white';

  const nameOverlayNoImageClass =
    colorScheme === 'dark'
      ? 'bg-white/5 text-gray-100'
      : 'bg-black/5 text-gray-800';

  const buttonClass =
    'inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold ' +
    'bg-[#0073aa] text-white hover:bg-[#0088cc] transition-colors w-full max-w-xs';

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <p className="text-sm text-gray-400">Loading local businesses...</p>
      </div>
    );
  }

  if (error || traders.length === 0) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <p className="text-sm text-gray-400">No businesses available for the scroll strip.</p>
      </div>
    );
  }

  // Duplicate traders to create a seamless infinite scroll effect.
  const displayTraders = [...traders, ...traders];

  return (
    <div
      ref={containerRef}
      className="relative w-full h-64 overflow-hidden"
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
    >
      <div
        ref={trackRef}
        className="absolute inset-y-0 left-0 flex h-full will-change-transform"
        style={{ transform: 'translateX(0px)' }}
      >
        {displayTraders.map((trader, index) => {
          const key = `${trader.id}-${index}`;
          const hasImage = !!trader.avatar_url;

          return (
            <div
              key={key}
              className={`flex-shrink-0 w-[250px] mr-5 flex flex-col rounded-lg border overflow-hidden h-full ${cardBackgroundClass}`}
            >
              <div className="relative w-full h-[180px] overflow-hidden">
                {hasImage ? (
                  <>
                    <img
                      src={getAvatar(trader)}
                      alt={trader.store_name}
                      className="w-full h-full object-cover"
                    />
                    <div
                      className={`absolute inset-x-0 top-0 px-4 py-3 text-sm font-semibold ${nameOverlayClass}`}
                    >
                      {trader.store_name}
                    </div>
                  </>
                ) : (
                  <div
                    className={`flex items-center justify-center w-full h-full px-4 py-3 text-sm font-semibold text-center ${nameOverlayNoImageClass}`}
                  >
                    {trader.store_name}
                  </div>
                )}
              </div>

              <div className="flex-1 flex flex-col justify-end p-4">
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${getBusinessTypeBadgeClass(
                      trader.businessType
                    )}`}
                  >
                    {getBusinessTypeLabel(trader.businessType)}
                  </span>
                  {trader.location && (
                    <p className="text-xs text-gray-400 ml-2 line-clamp-1">{trader.location}</p>
                  )}
                </div>
                <div className="flex justify-center">
                  <Link
                    to={getDetailLink(trader)}
                    className={buttonClass}
                  >
                    {buttonText}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TraderScrollStrip;
